import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LatLngLiteral } from "leaflet";

import { useTypedSelector } from "../../../../../redux/store";
import { PolygonList } from "../../Preferences/tabs/PolygonsEditor/type";
import { DefaultCity } from "../../../../../redux/reducers/map.reducer";
import Order from "../../../../../services/Order";
import MapService from "../../../../../services/Map";
import { getOrderPassengerPoints } from "../../../../../redux/constants/OrdersPage/order";

export type OrderPassengerPoints = {
	coordinates: LatLngLiteral;
	country: string;
	countryCode: string;
	customHouse: string;
	customStreet: string;
	district: string;
	entrance: string;
	flat: string;
	house: string;
	name: string;
	region: string;
	sector: undefined;
	settlement: string;
	settlementType: string;
	street: string;
	streetType: string;
	type: string;
};

export type UseMapReducer = {
	polygons: PolygonList;
	defaultCity: DefaultCity;
	route: LatLngLiteral[][];
	points: OrderPassengerPoints[];
	distance: number;
};

/**
 *  `useMapReducer`
 */
export const useMapReducer = (order: Order.Model | null): UseMapReducer => {
	const { defaultCity } = useTypedSelector((state) => state.mapReducer);
	const polygons = useTypedSelector((state) => state.mapReducer.polygons);

	const [segments, setSegments] = useState<LatLngLiteral[][]>([]);

	const points = useMemo(() => {
		if (!order) return [];
		return getOrderPassengerPoints(order);
	}, [order]);

	const pointsHash = useMemo(
		() =>
			JSON.stringify(
				points.map((point) => ({
					lat: point?.coordinates?.lat,
					lng: point?.coordinates?.lng,
				})),
			),
		[points],
	);

	// Drop the cached polyline whenever the stop list changes. Otherwise a
	// mid-edit / post-save point add keeps painting the previous N-1 route
	// (`segments` wins over `order.route` below) even though the price was
	// already recalculated for the new waypoints.
	const prevPointsHashRef = useRef(pointsHash);
	useEffect(() => {
		if (prevPointsHashRef.current === pointsHash) return;
		prevPointsHashRef.current = pointsHash;
		setSegments([]);
	}, [pointsHash]);

	const routeWaypointCount = useMemo(() => {
		const routePoints = order?.route?.points;
		if (Array.isArray(routePoints) && routePoints.length) {
			return routePoints.length;
		}
		// Compressed / client-built routes often only expose segments; one
		// segment between each consecutive pair of stops.
		const segmentCount = order?.route?.segments?.length ?? 0;
		return segmentCount > 0 ? segmentCount + 1 : 0;
	}, [order?.route]);

	const orderRouteMatchesPoints = useMemo(() => {
		if (points.length <= 1) return true;
		if (!routeWaypointCount) return false;
		return routeWaypointCount === points.length;
	}, [points.length, routeWaypointCount]);

	const getSegments = useCallback(async () => {
		if (points.length <= 1) return;

		const coordinates = points.map((point) => point?.coordinates);

		// Prefer a live geometry built from the current stops. `getOrderRouteById`
		// reads the server-side prepared route, which still reflects the
		// pre-edit stops until Save finishes — using it mid-edit (or right
		// after a point change) would snap the polyline back to the old path.
		const fromPoints = await MapService.getSegmentsAndPointsRoute(
			coordinates,
			true,
		);
		if (fromPoints?.segments?.length) {
			setSegments(fromPoints.segments ?? []);
			return;
		}

		if (order?.id) {
			const fromServer = await MapService.getOrderRouteById(order.id);
			if (fromServer?.segments?.length) {
				setSegments(fromServer.segments ?? []);
			}
		}
	}, [order?.id, points]);

	const route = useMemo(() => {
		if (points.length <= 1) return [];

		if (segments?.length) return segments;

		if (order?.route?.segments?.length && orderRouteMatchesPoints) {
			let isWrong = false;
			order.route.segments.forEach((item) => {
				if (typeof item === "string") isWrong = true;
			});
			if (isWrong) {
				setSegments(order.route.segments as LatLngLiteral[][]);
				return [];
			}
			return order.route.segments as LatLngLiteral[][];
		}

		getSegments();
		return [];
	}, [getSegments, order, orderRouteMatchesPoints, points.length, segments]);

	const distance = useMemo(() => {
		if (!order || !order.route) return 0;
		return order.route.distance || 0;
	}, [order]);

	return useMemo<UseMapReducer>(
		() => ({ polygons, defaultCity, route, points, distance }),
		[polygons, defaultCity, route, points, distance],
	);
};

export default useMapReducer;
